/**
 * Cloudflare integration utilities for frontend
 */

// Cloudflare Analytics
class CloudflareAnalytics {
  constructor(options = {}) {
    this.endpoint = options.endpoint || '/analytics/collect';
    this.sessionId = this.generateSessionId();
    this.queue = [];
    this.flushInterval = options.flushInterval || 5000;
    this.maxQueueSize = options.maxQueueSize || 10;
    
    this.startAutoFlush();
    this.setupPageTracking();
  }

  generateSessionId() {
    return crypto.randomUUID();
  }

  track(event, data = {}) {
    const eventData = {
      type: event,
      timestamp: Date.now(),
      sessionId: this.sessionId,
      path: window.location.pathname,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      ...data
    };

    this.queue.push(eventData);

    if (this.queue.length >= this.maxQueueSize) {
      this.flush();
    }
  }

  async flush() {
    if (this.queue.length === 0) return;

    const events = [...this.queue];
    this.queue = [];

    try {
      await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
      });
    } catch (error) {
      console.error('Analytics flush failed:', error);
      // Add events back to queue
      this.queue.unshift(...events);
    }
  }

  startAutoFlush() {
    setInterval(() => this.flush(), this.flushInterval);
    
    // Flush on page unload
    window.addEventListener('beforeunload', () => this.flush());
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    });
  }

  setupPageTracking() {
    // Track page views
    this.track('page_view');

    // Track navigation
    let currentPath = window.location.pathname;
    const observer = new MutationObserver(() => {
      if (window.location.pathname !== currentPath) {
        currentPath = window.location.pathname;
        this.track('page_view');
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // File-specific tracking methods
  trackFileUpload(fileData) {
    this.track('file_upload_start', {
      fileName: fileData.name,
      fileSize: fileData.size,
      fileType: fileData.type
    });
  }

  trackUploadProgress(progress, fileData) {
    this.track('upload_progress', {
      fileName: fileData.name,
      progress,
      timestamp: Date.now()
    });
  }

  trackUploadComplete(result) {
    this.track('file_upload_complete', {
      shareId: result.shareId,
      fileName: result.filename,
      fileSize: result.size,
      expiresAt: result.expiresAt
    });
  }

  trackDownloadStart(fileInfo) {
    this.track('download_start', {
      shareId: fileInfo.shareId,
      fileName: fileInfo.filename,
      fileSize: fileInfo.size
    });
  }

  trackDownloadComplete(fileInfo) {
    this.track('download_complete', {
      shareId: fileInfo.shareId,
      fileName: fileInfo.filename,
      fileSize: fileInfo.size
    });
  }

  trackError(error, context = {}) {
    this.track('error', {
      error: error.message,
      stack: error.stack,
      context
    });
  }
}

// Cloudflare Images integration
class CloudflareImages {
  constructor(options = {}) {
    this.accountHash = options.accountHash || '';
    this.baseUrl = options.baseUrl || 'https://imagedelivery.net';
    this.defaultVariant = options.defaultVariant || 'preview';
  }

  getImageUrl(imageId, variant = null, options = {}) {
    const variantName = variant || this.defaultVariant;
    let url = `${this.baseUrl}/${this.accountHash}/${imageId}/${variantName}`;

    // Add custom parameters
    const params = new URLSearchParams();
    if (options.width) params.set('w', options.width);
    if (options.height) params.set('h', options.height);
    if (options.quality) params.set('q', options.quality);
    if (options.format) params.set('f', options.format);
    if (options.fit) params.set('fit', options.fit);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    return url;
  }

  async uploadImage(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    if (options.metadata) {
      formData.append('metadata', JSON.stringify(options.metadata));
    }

    try {
      const response = await fetch('/cf-images/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Image upload failed:', error);
      throw error;
    }
  }

  generateResponsiveImageSet(imageId, sizes = [400, 800, 1200, 1600]) {
    return sizes.map(size => ({
      src: this.getImageUrl(imageId, null, { width: size }),
      width: size,
      descriptor: `${size}w`
    }));
  }
}

// Performance monitoring
class PerformanceMonitor {
  constructor(analytics) {
    this.analytics = analytics;
    this.setupPerformanceTracking();
  }

  setupPerformanceTracking() {
    // Track Core Web Vitals
    this.trackCoreWebVitals();
    
    // Track resource loading
    this.trackResourceTiming();
    
    // Track long tasks
    this.trackLongTasks();
  }

  trackCoreWebVitals() {
    // Largest Contentful Paint (LCP)
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      
      this.analytics.track('performance_lcp', {
        value: lastEntry.startTime,
        rating: this.getRating(lastEntry.startTime, [2500, 4000])
      });
    }).observe({ entryTypes: ['largest-contentful-paint'] });

    // First Input Delay (FID)
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        this.analytics.track('performance_fid', {
          value: entry.processingStart - entry.startTime,
          rating: this.getRating(entry.processingStart - entry.startTime, [100, 300])
        });
      });
    }).observe({ entryTypes: ['first-input'] });

    // Cumulative Layout Shift (CLS)
    let clsValue = 0;
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      
      this.analytics.track('performance_cls', {
        value: clsValue,
        rating: this.getRating(clsValue, [0.1, 0.25])
      });
    }).observe({ entryTypes: ['layout-shift'] });
  }

  trackResourceTiming() {
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        if (entry.initiatorType === 'fetch' || entry.initiatorType === 'xmlhttprequest') {
          this.analytics.track('api_performance', {
            url: entry.name,
            duration: entry.duration,
            size: entry.transferSize
          });
        }
      });
    }).observe({ entryTypes: ['resource'] });
  }

  trackLongTasks() {
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        this.analytics.track('performance_long_task', {
          duration: entry.duration,
          startTime: entry.startTime
        });
      });
    }).observe({ entryTypes: ['longtask'] });
  }

  getRating(value, thresholds) {
    if (value <= thresholds[0]) return 'good';
    if (value <= thresholds[1]) return 'needs-improvement';
    return 'poor';
  }
}

// Error tracking with Cloudflare integration
class ErrorTracker {
  constructor(analytics) {
    this.analytics = analytics;
    this.setupErrorTracking();
  }

  setupErrorTracking() {
    // Global error handler
    window.addEventListener('error', (event) => {
      this.analytics.trackError(event.error, {
        type: 'javascript_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.analytics.trackError(new Error(event.reason), {
        type: 'unhandled_promise_rejection'
      });
    });

    // Network errors
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        
        if (!response.ok) {
          this.analytics.track('network_error', {
            url: args[0],
            status: response.status,
            statusText: response.statusText
          });
        }
        
        return response;
      } catch (error) {
        this.analytics.track('network_error', {
          url: args[0],
          error: error.message
        });
        throw error;
      }
    };
  }
}

// Initialize Cloudflare integrations
const cloudflareAnalytics = new CloudflareAnalytics({
  endpoint: 'https://wasabi-storage-backend.hostings.workers.dev/analytics/collect'
});

const cloudflareImages = new CloudflareImages({
  accountHash: process.env.REACT_APP_CF_IMAGES_ACCOUNT || '',
  defaultVariant: 'preview'
});

const performanceMonitor = new PerformanceMonitor(cloudflareAnalytics);
const errorTracker = new ErrorTracker(cloudflareAnalytics);

export {
  cloudflareAnalytics,
  cloudflareImages,
  performanceMonitor,
  errorTracker,
  CloudflareAnalytics,
  CloudflareImages,
  PerformanceMonitor,
  ErrorTracker
}; 